import { FrameSourceState, RectangularViewfinder, RectangularViewfinderStyle } from '@scandit/web-datacapture-core'
import type { DataCaptureView } from '@scandit/web-datacapture-core'
import {
  CapturedLabel,
  type LabelCapture,
  LabelCaptureBasicOverlay,
  LabelCaptureFeedback,
  type LabelCaptureListener,
  LabelCaptureSettings,
  LabelDefinition,
} from '@scandit/web-datacapture-label'
import type { LabelPreset } from '../LabelCaptureSettingsStore'
import type { BuildSettingsContext, ControllerDeps, PresetController, Teardown } from './PresetController'

export class VinCodeController implements PresetController {
  readonly preset: LabelPreset = 'vinCode'

  private readonly seenTrackingIds = new Set<number>()

  constructor(private readonly deps: ControllerDeps) {}

  handleCapturedLabel(label: CapturedLabel, mode?: LabelCapture): void {
    if (this.seenTrackingIds.has(label.trackingId)) return
    this.seenTrackingIds.add(label.trackingId)
    this.deps.listStore.addFromCapturedVinLabel(label)
    if (mode) void this.pause(mode)
  }

  async pause(mode: LabelCapture): Promise<void> {
    await mode.setEnabled(false)
    await mode.context?.frameSource?.switchToDesiredState(FrameSourceState.Standby)
  }

  async resume(mode: LabelCapture): Promise<void> {
    this.seenTrackingIds.clear()
    await mode.setEnabled(true)
    await mode.context?.frameSource?.switchToDesiredState(FrameSourceState.On)
  }

  async buildSettings(ctx: BuildSettingsContext): Promise<LabelCaptureSettings> {
    const labelDefinition = await LabelDefinition.createVinLabelDefinition('vin', ctx.adaptiveRecognitionMode)
    return LabelCaptureSettings.fromLabelDefinitions([labelDefinition])
  }

  async activate(mode: LabelCapture, view: DataCaptureView): Promise<Teardown> {
    mode.feedback = LabelCaptureFeedback.default

    const overlay = await LabelCaptureBasicOverlay.withLabelCaptureForView(mode, view)
    await overlay.setViewfinder(new RectangularViewfinder(RectangularViewfinderStyle.Square))

    this.seenTrackingIds.clear()
    const listener: LabelCaptureListener = {
      didUpdateSession: (_mode, session) => {
        if (session.capturedLabels.length !== 1) return
        this.handleCapturedLabel(session.capturedLabels[0], mode)
      },
    }
    mode.addListener(listener)

    await mode.setEnabled(true)
    await mode.context?.frameSource?.switchToDesiredState(FrameSourceState.On)

    return async () => {
      mode.removeListener(listener)
      await view.removeOverlay(overlay)
    }
  }
}
